import React, { useContext } from 'react';
import {
  BrowserRouter as Router,
  Redirect,
  Switch,
  Route,
} from 'react-router-dom';
import loadable, { LoadableComponent } from '@loadable/component';
import MainLayout from '../layouts/MainLayout';
import { KeycloakUser, UserContext } from '../shared/user';

interface RouteInfo {
  path: string;
  exact?: boolean;
  component: LoadableComponent<unknown>;
}

interface RedirectInfo {
  from: string;
  to: string;
}

const authorRoutes: Array<RouteInfo | RedirectInfo> = [
  {
    path: '/',
    exact: true,
    component: loadable(() =>
      import(/* webpackChunkName: "HomePage" */ '../pages/home/HomePage'),
    ),
  },
  {
    path: '/articles',
    exact: true,
    component: loadable(() =>
      import(
        /* webpackChunkName: "ArticlesListPage" */ '../pages/articles/ArticlesListPage'
      ),
    ),
  },
  {
    path: '/articles/new',
    component: loadable(() =>
      import(
        /* webpackChunkName: "CreateArticlePage" */ '../pages/articles/CreateArticlePage'
      ),
    ),
  },
  {
    path: '/articles/:articleId',
    component: loadable(() =>
      import(
        /* webpackChunkName: "EditArticlePage" */ '../pages/articles/EditArticlePage'
      ),
    ),
  },
  {
    path: '/articles/:articleId/preview',
    exact: true,
    component: loadable(() =>
      import(
        /* webpackChunkName: "ArticlePreviewPage" */ '../pages/articles/ArticlePreviewPage'
      ),
    ),
  },
  {
    path: '/attachments',
    exact: true,
    component: loadable(() =>
      import(
        /* webpackChunkName: "AttachmentsListPage" */ '../pages/attachments/AttachmentsListPage'
      ),
    ),
  },
  {
    path: '/attachments/new',
    component: loadable(() =>
      import(
        /* webpackChunkName: "CreateAttachmentPage" */ '../pages/attachments/CreateAttachmentPage'
      ),
    ),
  },
  {
    path: '/attachments/:attachmentId',
    component: loadable(() =>
      import(
        /* webpackChunkName: "EditAttachmentPage" */ '../pages/attachments/EditAttachmentPage'
      ),
    ),
  },
  {
    path: '/videos',
    exact: true,
    component: loadable(() =>
      import(
        /* webpackChunkName: "VideosListPage" */ '../pages/videos/VideosListPage'
      ),
    ),
  },
  {
    path: '/videos/new',
    component: loadable(() =>
      import(
        /* webpackChunkName: "CreateVideoPage" */ '../pages/videos/CreateVideoPage'
      ),
    ),
  },
  {
    path: '/videos/:videoId',
    component: loadable(() =>
      import(
        /* webpackChunkName: "EditVideoPage" */ '../pages/videos/EditVideoPage'
      ),
    ),
  },
  {
    path: '/video-series/:videoSeriesId',
    component: loadable(() =>
      import(
        /* webpackChunkName: "EditVideoSeriesPage" */ '../pages/video-series/EditVideoSeriesPage'
      ),
    ),
  },
  {
    path: '/faqs/new',
    component: loadable(() =>
      import(
        /* webpackChunkName: "CreateFaqsPage" */ '../pages/faqs/CreateFAQsPage'
      ),
    ),
  },
  {
    path: '/faqs/:faqId',
    component: loadable(() =>
      import(
        /* webpackChunkName: "EditFaqsPage" */ '../pages/faqs/EditFAQsPage'
      ),
    ),
  },
  {
    path: '/faqs',
    exact: true,
    component: loadable(() =>
      import(
        /* webpackChunkName: "FAQsListPage" */ '../pages/faqs/FAQsListPage'
      ),
    ),
  },
  // {
  //   path: '/events',
  //   exact: true,
  //   component: loadable(() =>
  //     import(
  //       /* webpackChunkName: "EventsListPage" */ '../pages/events/EventsListPage'
  //     ),
  //   ),
  // },
  {
    path: '/events/new',
    component: loadable(() =>
      import(
        /* webpackChunkName: "CreateEventPage" */ '../pages/events/CreateEventPage'
      ),
    ),
  },
  {
    path: '/events/:eventId',
    component: loadable(() =>
      import(
        /* webpackChunkName: "EditEventPage" */ '../pages/events/EditEventPage'
      ),
    ),
  },
  {
    path: '/courses',
    exact: true,
    component: loadable(() =>
      import(
        /* webpackChunkName: "EventsListPage" */ '../pages/courses/CourseListPage'
      ),
    ),
  },
  {
    path: '/courses/new',
    component: loadable(() =>
      import(
        /* webpackChunkName: "CreateEventPage" */ '../pages/courses/CreateCoursePage'
      ),
    ),
  },
  {
    path: '/courses/:courseId',
    component: loadable(() =>
      import(
        /* webpackChunkName: "EditCoursePage" */ '../pages/courses/EditCoursePage'
      ),
    ),
  },
  {
    path: '/exams',
    exact: true,
    component: loadable(() =>
      import(
        /* webpackChunkName: "ExamsListPage" */ '../pages/exams/ExamsListPage'
      ),
    ),
  },
  {
    path: '/exams/new',
    exact: true,
    component: loadable(() =>
      import(
        /* webpackChunkName: "CreateExamPage" */ '../pages/exams/CreateExamPage'
      ),
    ),
  },
  {
    path: '/exams/upload',
    exact: true,
    component: loadable(() =>
      import(
        /* webpackChunkName: "CSVUploadPage" */ '../pages/exams/CSVUploadPage'
      ),
    ),
  },
  {
    path: '/exams/:examId',
    exact: true,
    component: loadable(() =>
      import(
        /* webpackChunkName: "EditExamPage" */ '../pages/exams/EditExamPage'
      ),
    ),
  },
  {
    path: '/exams/:examId/translation/:translationCode',
    exact: true,
    component: loadable(() =>
      import(
        /* webpackChunkName: "ExamTranslation" */ '../pages/exams/TranslationExamPage'
      ),
    ),
  },
  {
    path: '/diploma-templates',
    exact: true,
    component: loadable(() =>
      import(
        /* webpackChunkName: "DiplomaTemplateListPage" */ '../pages/diploma-templates/DiplomaTemplateListPage'
      ),
    ),
  },
  {
    path: '/diploma-templates/new',
    exact: true,
    component: loadable(() =>
      import(
        /* webpackChunkName: "CreateDiplomaTemplatePage" */ '../pages/diploma-templates/CreateDiplomaTemplatePage'
      ),
    ),
  },
  {
    path: '/diploma-templates/:diplomaTemplateId',
    exact: true,
    component: loadable(() =>
      import(
        /* webpackChunkName: "EditDiplomaTemplatePage" */ '../pages/diploma-templates/EditDiplomaTemplatePage'
      ),
    ),
  },
  {
    path: '/news',
    exact: true,
    component: loadable(() =>
      import(
        /* webpackChunkName: "DiplomaTemplateListPage" */ '../pages/news/NewsListPage'
      ),
    ),
  },
  {
    path: '/news/new',
    exact: true,
    component: loadable(() =>
      import(
        /* webpackChunkName: "DiplomaTemplateListPage" */ '../pages/news/CreateNewsPage'
      ),
    ),
  },
  {
    path: '/news/:newsId',
    exact: true,
    component: loadable(() =>
      import(
        /* webpackChunkName: "DiplomaTemplateListPage" */ '../pages/news/EditNewsPage'
      ),
    ),
  },
];

const ownerRoutes: Array<RouteInfo | RedirectInfo> = [
  {
    path: '/companies/new',
    component: loadable(() =>
      import(
        /* webpackChunkName: "CreateCompanyPage" */ '../pages/companies/CreateCompanyPage'
      ),
    ),
  },
  {
    path: '/companies/:companyId',
    component: loadable(() =>
      import(
        /* webpackChunkName: "EditCompanyPage" */ '../pages/companies/EditCompanyPage'
      ),
    ),
  },
  {
    path: '/companies',
    exact: true,
    component: loadable(() =>
      import(
        /* webpackChunkName: "CompanyListPage" */ '../pages/companies/CompanyListPage'
      ),
    ),
  },
  {
    path: '/users/new',
    component: loadable(() =>
      import(
        /* webpackChunkName: "CreateUserPage" */ '../pages/users/CreateUserPage'
      ),
    ),
  },
  {
    path: '/users/:userId',
    component: loadable(() =>
      import(
        /* webpackChunkName: "EditUserPage" */ '../pages/users/EditUserPage'
      ),
    ),
  },
  {
    path: '/users',
    exact: true,
    component: loadable(() =>
      import(
        /* webpackChunkName: "UserListPage" */ '../pages/users/UserListPage'
      ),
    ),
  },
  {
    path: '/exams',
    exact: true,
    component: loadable(() =>
      import(
        /* webpackChunkName: "UserWithExamsListPage" */ '../pages/users-exams/UserWithExamsListPage'
      ),
    ),
  },
  {
    from: '/',
    to: '/companies',
  },
];

const bhtConsultantRoutes: Array<RouteInfo | RedirectInfo> = [
  {
    path: '/bht-documents',
    exact: true,
    component: loadable(() =>
      import(
        /* webpackChunkName: "BhtDocumentsListPage" */ '../pages/bht-documents/BhtDocumentsListPage'
      ),
    ),
  },
  {
    path: '/bht-documents/new',
    component: loadable(() =>
      import(
        /* webpackChunkName: "CreateBhtDocumentPage" */ '../pages/bht-documents/CreateBhtDocumentPage'
      ),
    ),
  },
  {
    path: '/bht-documents/:bhtDocumentId',
    component: loadable(() =>
      import(
        /* webpackChunkName: "EditBhtDocumentPage" */ '../pages/bht-documents/EditBhtDocumentPage'
      ),
    ),
  },
  {
    from: '',
    to: '/bht-documents',
  },
];

/**
 * Return the routes based on user role.
 */              
const getRoutes = (userInfo: KeycloakUser): Array<RouteInfo | RedirectInfo> => {
  if (userInfo._isAuthor) {
    return authorRoutes;
  }

  if (userInfo._isOwner) {
    return ownerRoutes;
  }

  if (userInfo._isBHTConsultant) {
    return bhtConsultantRoutes;
  }

  return [];
};

const createRoute = (routeInfo: RouteInfo | RedirectInfo): JSX.Element => {
  if ((routeInfo as RouteInfo).path) {
    const { exact, path, component } = routeInfo as RouteInfo;
    return (
      <Route exact={!!exact} path={path} key={path} component={component} />
    );
  }
  const { from, to } = routeInfo as RedirectInfo;
  return <Redirect from={from} to={to} key={from} />;
};

/**
 * A component for the routings with page components loaded on demand.
 */
const Routes = (): JSX.Element => {
  const userInfo = useContext(UserContext);

  return (
    <Router>
      <MainLayout>
        <Switch>{getRoutes(userInfo).map(createRoute)}</Switch>
      </MainLayout>
    </Router>
  );
};

export default Routes;
